import React, { useEffect, useRef, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { DoubleSide } from 'three';

interface VideoSceneProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  scrollProgress?: number;
}

export const VideoScene: React.FC<VideoSceneProps> = ({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  scrollProgress = 0
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const videoTextureRef = useRef<THREE.VideoTexture | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Memoize geometry to prevent recreation
  const geometry = useMemo(() => new THREE.PlaneGeometry(16, 9), []);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    handleResize();
    
    // Listen for window resize
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Create and reuse video element
    const video = document.createElement('video');
    video.src = '/assets/video/screenshot.mp4';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    
    // Set video properties for better performance
    video.preload = 'auto';
    video.style.objectFit = 'cover';
    
    // Create video texture with optimized settings
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.format = THREE.RGBAFormat; // Updated from RGBFormat
    videoTexture.generateMipmaps = false; // Disable mipmaps for video texture
    videoTexture.colorSpace = THREE.SRGBColorSpace;
    
    // Create and reuse material
    const material = new THREE.MeshBasicMaterial({ 
      map: videoTexture,
      side: DoubleSide,
      toneMapped: false,
      transparent: true,
      opacity: 0.9
    });

    if (meshRef.current) {
      meshRef.current.material = material;
    }
    
    videoTextureRef.current = videoTexture;
    videoRef.current = video;
    
    // Start video playback
    video.load();
    video.play().catch(console.warn);

    return () => {
      video.pause();
      video.remove();
      videoTexture.dispose();
      material.dispose();
    };
  }, []);

  // Cache transform values to reduce calculations
  const transformCache = useRef({
    scale: new THREE.Vector3(),
    position: new THREE.Vector3(),
    rotation: new THREE.Euler()
  });

  // Update texture and handle animations on each frame
  useFrame(() => {
    if (!meshRef.current) return;

    // Only update video texture when video is playing
    if (videoRef.current?.readyState === 4 && videoTextureRef.current) {
      videoTextureRef.current.needsUpdate = true;
    }
    
    // Calculate scale with caching
    const baseScale = scale[0];
    const scaleProgress = Math.pow(scrollProgress, 0.8);
    
    // Apply different scaling factors based on device
    const scaleEffect = isMobile 
      ? 0.4 + (scaleProgress * 0.8) // Smaller scale factor for mobile
      : 0.5 + (scaleProgress * 1.2); // Original scale factor
    
    const newScale = baseScale * scaleEffect;
    
    transformCache.current.scale.set(newScale, newScale, newScale);
    meshRef.current.scale.copy(transformCache.current.scale);
    
    // Calculate rotation - less rotation on mobile
    const rotYFactor = isMobile ? 0.3 : 0.6;
    const rotXFactor = isMobile ? 0.15 : 0.3;
    const rotY = -0.3 + (scrollProgress * rotYFactor);
    const rotX = -0.15 + (scrollProgress * rotXFactor);
    transformCache.current.rotation.set(rotX, rotY, 0);
    meshRef.current.rotation.copy(transformCache.current.rotation);
    
    // Calculate position with floating effect - reduced on mobile
    const time = Date.now() * 0.0008;
    const floatFactor = isMobile ? 0.5 : 1.0; // Reduce float effect on mobile
    const floatY = Math.sin(time) * 0.08 * (1 - scrollProgress) * floatFactor;
    const floatX = Math.cos(time * 0.7) * 0.05 * (1 - scrollProgress) * floatFactor;
    
    transformCache.current.position.set(
      position[0] + floatX,
      position[1] + floatY,
      position[2]
    );
    meshRef.current.position.copy(transformCache.current.position);
  });

  return (
    <mesh
      ref={meshRef}
      position={new THREE.Vector3(...position)}
      rotation={new THREE.Euler(...rotation)}
      scale={new THREE.Vector3(...scale)}
      geometry={geometry}
    >
      <meshBasicMaterial side={DoubleSide} />
    </mesh>
  );
};

export default VideoScene; 